import { Controller } from "react-hook-form";
import { CheckIcon, Info } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";

import { SelectFieldProps } from "./types";

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { BasicTooltip } from "@/components/ui/Tooltip";

const selectFieldStyles = {
  container: cva("flex flex-col", {
    variants: {
      size: {
        desktop: "gap-1.5",
        dialog: "gap-1",
      },
      fullWidth: {
        true: "",
        false: "max-w-[400px]",
      },
    },
    defaultVariants: {
      size: "desktop",
      fullWidth: false,
    },
  }),
  label: cva("font-medium text-gray-900", {
    variants: {
      size: {
        desktop: "text-base",
        dialog: "text-sm",
      },
    },
    defaultVariants: {
      size: "desktop",
    },
  }),
  trigger: cva("w-full rounded-lg", {
    variants: {
      size: {
        desktop: "h-11 px-3.5 py-2.5",
        dialog: "h-9 px-3 py-2",
      },
    },
    defaultVariants: {
      size: "desktop",
    },
  }),
  triggerText: cva("text-gray-900", {
    variants: {
      size: {
        desktop: "text-base",
        dialog: "text-sm",
      },
    },
    defaultVariants: {
      size: "desktop",
    },
  }),
  checkIcon: cva("ml-2 text-blue-500", {
    variants: {
      size: {
        desktop: "h-4 w-4",
        dialog: "h-3 w-3",
      },
    },
    defaultVariants: {
      size: "desktop",
    },
  }),
};

export type SelectFieldStyleProps = VariantProps<typeof selectFieldStyles.container>;

export const SelectField = ({ field, form, stepId, size = "desktop" }: SelectFieldProps) => {
  const fieldsInputs = form.watch(stepId);
  const items =
    typeof field.fieldOptions === "function"
      ? field.fieldOptions(fieldsInputs)
      : field.fieldOptions || [];
  return (
    <Controller
      name={`${stepId}.${field.id}`}
      control={form.control}
      rules={{
        required: field.required,
        validate: value => field.validation?.(value, fieldsInputs),
      }}
      render={({ field: formField, fieldState: { error, isDirty } }) => (
        <div
          className={selectFieldStyles.container({ size, fullWidth: field.fullWidth ?? false })}
          key={field.id}
        >
          <div className="flex items-center gap-2">
            <label className={selectFieldStyles.label({ size })}>
              {typeof field.label === "function" ? field.label(fieldsInputs) : field.label}
            </label>
            {field.tooltip && (
              <BasicTooltip content={field.tooltip}>
                <Info className="h-4 w-4 cursor-pointer text-gray-500 hover:text-gray-700" />
              </BasicTooltip>
            )}
          </div>
          {error && isDirty && <p className="text-sm text-red-500">{error.message}</p>}
          <DropdownMenu>
            <DropdownMenuTrigger
              disabled={field.readOnly}
              className={selectFieldStyles.trigger({ size })}
            >
              <span className={selectFieldStyles.triggerText({ size })}>
                {items?.find(option => formField.value === option.value)?.title ||
                  (typeof field.placeholder === "function"
                    ? field.placeholder(fieldsInputs)
                    : field.placeholder)}
              </span>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {items?.map(option => (
                <DropdownMenuItem
                  key={option.value}
                  onSelect={() => {
                    formField.onChange(option.value);
                  }}
                >
                  {option.title}
                  {formField.value === option.value && (
                    <CheckIcon className={selectFieldStyles.checkIcon({ size })} />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    />
  );
};
